/**
 * Stripe service functions
 * 
 * This module provides functions for interacting with Stripe payment processing
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';

// Initialize Firebase Functions
const functions = getFunctions();

/**
 * Create a payment intent for an appointment
 * @param {Object} appointmentData - The appointment data
 * @param {string} appointmentId - The ID of the appointment
 * @param {number} amount - The amount to charge in pence (e.g., £50 = 5000)
 * @returns {Promise<Object>} Object containing the client secret
 */
export const createPaymentIntent = async (appointmentData, appointmentId, amount = null) => {
  try {
    // Format the appointment date and time for metadata
    const startTime = appointmentData.slot.startTime.toDate();
    const formattedTime = startTime.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    const formattedDate = startTime.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Call the cloud function
    const createPaymentIntentFn = httpsCallable(functions, 'createPaymentIntent');
    const result = await createPaymentIntentFn({
      amount,
      appointmentId,
      practiceId: appointmentData.practiceId,
      patientName: appointmentData.userInfo.name,
      appointmentDate: formattedDate,
      appointmentTime: formattedTime,
  
    });

    return result.data;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

/**
 * Cancel a payment for an appointment
 * @param {string} appointmentId - The ID of the appointment
 * @returns {Promise<Object>} Object indicating success or failure
 */
export const cancelPayment = async (appointmentId) => {
  try {
    const cancelPaymentFn = httpsCallable(functions, 'cancelPayment');
    const result = await cancelPaymentFn({ appointmentId });
    return result.data;
  } catch (error) {
    console.error('Error cancelling payment:', error);
    throw error;
  }
};

/**
 * Update the payment status of an appointment
 * @param {string} appointmentId - The ID of the appointment
 * @param {string} status - The new payment status
 * @param {string} [error] - Optional error message
 * @returns {Promise<void>}
 */
export const updatePaymentStatus = async (appointmentId, status, error = null) => {
  try {
    const appointmentRef = doc(db, 'appointments', appointmentId);
    
    const updateData = {
      paymentStatus: status
    };
    
    if (error) {
      updateData.paymentError = error;
    }
    
    await updateDoc(appointmentRef, updateData);
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw error;
  }
};
